import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import nameSort from 'scaleflow/utils/nameSort';
import TeamModel from 'scaleflow/models/team';
import PersonModel from 'scaleflow/models/person';
import DepartmentModel from 'scaleflow/models/department';

export interface DropdownsGoalOwnerSignature {
  Args: {
    value?: TeamModel | PersonModel | DepartmentModel; // Current owner (Person, Team, or Department)
    onSelect: (owner: any) => void; // Callback when owner selected
    tippy?: boolean;
    placement?: string;
    trigger?: string;
    interactive?: boolean;
    showClearSelection?: boolean;
    onAddPersonClick?: () => void; // Callback for add person
    onAddTeamClick?: () => void; // Callback for add team
    onAddDepartmentClick?: () => void; // Callback for add department
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class DropdownsGoalOwner extends Component<DropdownsGoalOwnerSignature> {
  @service declare store: any;
  @service declare intl: any;

  @tracked activeView: 'users' | 'teams' | 'departments' =
    (this.args.value as any)?.content?.constructor?.modelName === 'team'
      ? 'teams'
      : (this.args.value as any)?.content?.constructor?.modelName ===
          'department'
        ? 'departments'
        : 'users';
  @tracked textFilter = '';
  @tracked tippyInstance: any = null;

  // Get active users from store
  get users() {
    return this.store
      .peekAll('person')
      .filter((person: any) => person.active)
      .sort((a: any, b: any) => nameSort(a.name as string, b.name as string));
  }

  // Get active teams from store
  get teams() {
    return this.store
      .peekAll('team')
      .filter((team: any) => !team.archiveTime)
      .sort((a: any, b: any) => nameSort(a.name as string, b.name as string));
  }

  // Get departments from store
  get departments() {
    return this.store
      .peekAll('department')
      .toSorted((a: any, b: any) =>
        nameSort(a.name as string, b.name as string),
      );
  }

  // Filter users by search text
  get filteredUsers() {
    if (!this.textFilter) return this.users;
    const filter = this.textFilter.toLowerCase();
    return this.users.filter((user: any) =>
      user.name?.toLowerCase().includes(filter),
    );
  }

  // Filter teams by search text
  get filteredTeams() {
    if (!this.textFilter) return this.teams;
    const filter = this.textFilter.toLowerCase();
    return this.teams.filter((team: any) =>
      team.name?.toLowerCase().includes(filter),
    );
  }

  // Filter departments by search text
  get filteredDepartments() {
    if (!this.textFilter) return this.departments;
    const filter = this.textFilter.toLowerCase();
    return this.departments.filter((department: any) =>
      department.name?.toLowerCase().includes(filter),
    );
  }

  // Show search if there are enough items
  get showSearch() {
    if (this.activeView === 'users') return this.users.length >= 6;
    if (this.activeView === 'teams') return this.teams.length >= 6;
    if (this.activeView === 'departments') return this.departments.length >= 6;
    return false;
  }

  // Get current list based on active view
  get currentList() {
    if (this.activeView === 'users') return this.filteredUsers;
    if (this.activeView === 'teams') return this.filteredTeams;
    if (this.activeView === 'departments') return this.filteredDepartments;
    return [];
  }

  @action
  switchView(view: 'users' | 'teams' | 'departments') {
    this.activeView = view;
    this.textFilter = ''; // Clear search when switching
  }

  @action
  handleSearch(text: string) {
    this.textFilter = text;
  }

  @action
  handleSelect(item: any) {
    this.args.onSelect(item);
    this.tippyInstance?.hide();
  }

  @action
  handleClearSelection() {
    this.args.onSelect({ id: 'clear' });
    this.tippyInstance?.hide();
  }

  @action
  setTippyInstance(instance: any) {
    this.tippyInstance = instance;
  }

  @action
  onShown() {
    // Focus search input when dropdown opens
    const searchInput = document.querySelector('.goal-owner-search input');
    if (searchInput) {
      (searchInput as HTMLInputElement).focus();
    }
  }

  @action
  handleAddPerson() {
    if (this.args.onAddPersonClick) {
      this.args.onAddPersonClick();
    }
    this.tippyInstance?.hide();
  }

  @action
  handleAddTeam() {
    if (this.args.onAddTeamClick) {
      this.args.onAddTeamClick();
    }
    this.tippyInstance?.hide();
  }

  @action
  handleAddDepartment() {
    if (this.args.onAddDepartmentClick) {
      this.args.onAddDepartmentClick();
    }
    this.tippyInstance?.hide();
  }
}
